/* ==========================================================================
   assets/header.js
   Advanced Mega Menu Header — Rewrite: event delegation architecture
   ==========================================================================
   Why delegation: this script is loaded via a <script> tag embedded
   INSIDE the header section's own markup. Shopify's Theme Editor
   re-renders a section's HTML via AJAX on every settings change while
   you're editing (that's how the live preview works), and browsers do
   NOT execute <script> tags that arrive via that kind of DOM swap —
   only via a real page load. So if listeners were attached directly to
   specific elements at page-load time, every one of those elements
   stops working the moment the section re-renders once during editing.

   Fix: attach every listener ONCE, at the top level (document/window),
   which never gets swapped, and have each handler look up whatever
   elements it needs fresh at event time via closest()/querySelector()
   rather than caching references. This keeps working correctly no
   matter how many times the section's inner HTML gets replaced.

   Also: the desktop mega-menu drill-down and the mobile drill-down
   share the exact same screen contract (data-screen-id /
   data-parent-screen / data-drill-target / data-nav-back, with
   .is-active / .is-offleft class toggling) — see header-mega-drill.liquid
   and header-mobile-nav.liquid — so there's one shared goToScreen()
   function instead of two parallel implementations.
   ========================================================================== */

(function () {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusable(container) {
    if (!container) return [];
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        return el.offsetParent !== null;
      });
  }

  /* ======================================================================
     STICKY HEADER (show/hide on scroll)
     ====================================================================== */
  (function initSticky() {
    var lastY = window.scrollY;
    var threshold = 8;
    var ticking = false;

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.scrollY;
        document.querySelectorAll('[data-header][data-sticky="on_scroll_up"]').forEach(function (header) {
          var openPanel = header.querySelector('.mega-panel:not([hidden])');
          var mobileNav = header.querySelector('[data-header-mobile-nav]');
          var mobileOpen = mobileNav && !mobileNav.hidden;
          if (openPanel || mobileOpen) {
            header.classList.remove('site-header--hidden');
          } else if (y > lastY + threshold && y > header.offsetHeight) {
            header.classList.add('site-header--hidden');
          } else if (y < lastY - threshold) {
            header.classList.remove('site-header--hidden');
          }
        });
        lastY = y;
        ticking = false;
      });
    }, { passive: true });
  })();

  /* ======================================================================
     ANIMATED ACTIVE-ITEM INDICATOR
     ====================================================================== */
  function moveIndicator(navList, target) {
    var indicator = navList.querySelector('[data-nav-indicator]');
    if (!indicator) return;
    if (!target) {
      indicator.style.opacity = '0';
      return;
    }
    // getBoundingClientRect (not offsetLeft): offsetLeft is relative to
    // whichever positioned ancestor happens to be nearest a GIVEN
    // target, and that can differ per item (e.g. a mega-menu trigger's
    // <li> is itself position:relative so its dropdown panel can anchor
    // to it — see header.css — which means that one item's offsetLeft
    // means something different than a plain link's). getBoundingClientRect
    // always returns true viewport coordinates regardless of any
    // element's own position value, so comparing it against the
    // indicator's own offsetParent is correct for every item, always.
    var parent = indicator.offsetParent || navList;
    var parentRect = parent.getBoundingClientRect();
    var targetRect = target.getBoundingClientRect();
    indicator.style.width = targetRect.width + 'px';
    indicator.style.transform = 'translateX(' + (targetRect.left - parentRect.left) + 'px)';
    indicator.style.opacity = '1';
  }

  function currentAnchor(navList) {
    return navList.querySelector(
      '[data-mega-menu-trigger][aria-expanded="true"], .site-header__nav-link.is-active'
    );
  }

  document.addEventListener('mouseover', function (e) {
    var link = e.target.closest('.site-header__nav-link');
    if (!link) return;
    var navList = link.closest('.site-header__nav-list');
    if (navList) moveIndicator(navList, link);
  });

  document.addEventListener('focusin', function (e) {
    var link = e.target.closest('.site-header__nav-link');
    if (!link) return;
    var navList = link.closest('.site-header__nav-list');
    if (navList) moveIndicator(navList, link);
  });

  document.addEventListener('mouseout', function (e) {
    var navList = e.target.closest('.site-header__nav-list');
    if (!navList) return;
    if (navList.contains(e.relatedTarget)) return; // still inside, ignore
    moveIndicator(navList, currentAnchor(navList));
  });

  window.addEventListener('resize', function () {
    document.querySelectorAll('.site-header__nav-list').forEach(function (navList) {
      moveIndicator(navList, currentAnchor(navList));
    });
  }, { passive: true });

  /* ======================================================================
     SHARED DRILL-DOWN NAVIGATION
     Used by BOTH the mobile nav ([data-header-mobile-nav]) and every desktop
     mega-menu's drill region ([data-mega-drill]) — identical screen
     contract, so one implementation serves both.
     ====================================================================== */
  function goToScreen(container, targetId, direction) {
    var target = container.querySelector('[data-screen-id="' + targetId + '"]');
    var current = container.querySelector(
      '.header-mobile-nav__screen.is-active, .mega-drill__screen.is-active'
    );
    if (!target || target === current) return;

    if (direction === 'back') {
      target.style.transition = 'none';
      target.classList.remove('is-active');
      target.classList.add('is-offleft');
      void target.offsetWidth; // force reflow before re-enabling transition
      target.style.transition = '';

      if (current) {
        current.classList.remove('is-active');
        current.classList.remove('is-offleft');
      }

      requestAnimationFrame(function () {
        target.classList.remove('is-offleft');
        target.classList.add('is-active');
      });
    } else {
      target.classList.remove('is-offleft');
      if (current) {
        current.classList.remove('is-active');
        current.classList.add('is-offleft');
      }
      requestAnimationFrame(function () {
        target.classList.add('is-active');
      });
    }

    var focusable = getFocusable(target);
    if (focusable.length) focusable[0].focus();
  }

  document.addEventListener('click', function (e) {
    var drillBtn = e.target.closest('[data-drill-target]');
    if (drillBtn) {
      var container = drillBtn.closest('[data-header-mobile-nav], [data-mega-drill]');
      if (container) goToScreen(container, drillBtn.getAttribute('data-drill-target'), 'forward');
      return;
    }
    var backBtn = e.target.closest('[data-nav-back]');
    if (backBtn) {
      var backContainer = backBtn.closest('[data-header-mobile-nav], [data-mega-drill]');
      var currentScreen = backBtn.closest('.header-mobile-nav__screen, .mega-drill__screen');
      var parentId = currentScreen && currentScreen.getAttribute('data-parent-screen');
      if (backContainer && parentId) goToScreen(backContainer, parentId, 'back');
    }
  });

  // Up/Down arrow keys move focus within whichever screen currently has it
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    var screen = e.target.closest('.header-mobile-nav__screen, .mega-drill__screen');
    if (!screen) return;
    e.preventDefault();
    var items = getFocusable(screen);
    var idx = items.indexOf(document.activeElement);
    var next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
    if (next >= 0 && next < items.length) items[next].focus();
  });

  /* ======================================================================
     DESKTOP MEGA MENU — open / close
     ====================================================================== */
  var openPanelId = null;

  function getPanelById(id) {
    return document.getElementById('MegaPanel-' + id);
  }

  // "Nothing should be pre-selected" applies every time the menu is
  // reopened too, not just on first load — so reset every screen back
  // to Level 1 once the close transition finishes.
  function resetDrillToStart(panel) {
    var drill = panel.querySelector('[data-mega-drill]');
    if (!drill) return;
    var screens = drill.querySelectorAll('.mega-drill__screen');
    screens.forEach(function (s) {
      s.style.transition = 'none';
      s.classList.remove('is-active', 'is-offleft');
    });
    var first = drill.querySelector('.mega-drill__screen');
    if (first) first.classList.add('is-active');
    void drill.offsetWidth; // force reflow before restoring transitions
    screens.forEach(function (s) {
      s.style.transition = '';
    });
  }

  function closeMegaPanel(id, opts) {
    var silent = opts && opts.silent;
    var panel = getPanelById(id);
    var item = document.querySelector('[data-mega-menu-id="' + id + '"]');
    if (!panel || !item) return;
    var trigger = item.querySelector('[data-mega-menu-trigger]');

    panel.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');

    window.setTimeout(function () {
      panel.hidden = true;
      resetDrillToStart(panel);
    }, 220); // matches --header-speed-base

    if (openPanelId === id) openPanelId = null;

    var navList = item.closest('.site-header__nav-list');
    if (navList) moveIndicator(navList, currentAnchor(navList));

    if (!silent) trigger.focus();
  }

  function openMegaPanel(id) {
    document.querySelectorAll('[data-mega-menu-item]').forEach(function (item) {
      var otherId = item.getAttribute('data-mega-menu-id');
      if (otherId !== id && otherId === openPanelId) closeMegaPanel(otherId, { silent: true });
    });

    var panel = getPanelById(id);
    var item = document.querySelector('[data-mega-menu-id="' + id + '"]');
    if (!panel || !item) return;
    var trigger = item.querySelector('[data-mega-menu-trigger]');

    panel.hidden = false;
    void panel.offsetWidth; // force reflow so opening transition runs
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    openPanelId = id;

    var navList = item.closest('.site-header__nav-list');
    if (navList) moveIndicator(navList, trigger);
  }

  // Multi-column panel: clicking a Level 1 item swaps which Level 2
  // group is visible (and auto-reveals that group's first Level 2's
  // Level 3 group); clicking a Level 2 item swaps which Level 3 group
  // is visible. This is the "expanding left to right" behavior —
  // levels stay visible simultaneously, only which GROUP shows changes.
  function selectLevel1(panel, level1Id) {
    selectLevel2(panel, null);
    panel.querySelectorAll('[data-level1-trigger]').forEach(function (btn) {
      var match = btn.getAttribute('data-level1-id') === level1Id;
      btn.classList.toggle('is-active', match);
      btn.setAttribute('aria-current', match ? 'true' : 'false');
    });

    var activeGroup = null;
    panel.querySelectorAll('[data-level1-parent]').forEach(function (group) {
      var match = group.getAttribute('data-level1-parent') === level1Id;
      group.classList.toggle('is-active', match);
      group.hidden = !match;
      if (match) activeGroup = group;
    });

    if (activeGroup) {
      var firstL2 = activeGroup.querySelector('[data-level2-trigger]');
      if (firstL2) selectLevel2(panel, firstL2.getAttribute('data-level2-id'));
    }
  }

  function selectLevel2(panel, level2Id) {
    panel.querySelectorAll('[data-level2-trigger]').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-level2-id') === level2Id);
    });
    panel.querySelectorAll('[data-level2-parent]').forEach(function (group) {
      var match = group.getAttribute('data-level2-parent') === level2Id;
      group.classList.toggle('is-active', match);
      group.hidden = !match;
    });
  }

  function sortMenuItems() {
    var collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
    document.querySelectorAll('.mega-panel ul, .mega-drill__list, .header-mobile-nav__screen:not([data-screen-id="root"]) .header-mobile-nav__list').forEach(function (list) {
      var items = Array.from(list.children).filter(function (item) { return item.tagName === 'LI'; });
      function title(item) {
        var control = item.querySelector('button, a, span');
        var label = control && control.querySelector('span:not(.badge)');
        return (label || control || item).textContent.trim();
      }
      items.sort(function (a, b) { return collator.compare(title(a), title(b)); });
      items.forEach(function (item) { list.appendChild(item); });
    });
    document.querySelectorAll('.mega-panel').forEach(function (panel) {
      var active = panel.querySelector('[data-level1-trigger].is-active');
      if (active) selectLevel1(panel, active.getAttribute('data-level1-id'));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sortMenuItems, { once: true });
  else sortMenuItems();
  document.addEventListener('shopify:section:load', sortMenuItems);

  document.addEventListener('click', function (e) {
    var l1btn = e.target.closest('[data-level1-trigger]');
    if (l1btn) {
      var l1Panel = l1btn.closest('.mega-panel');
      if (l1Panel) selectLevel1(l1Panel, l1btn.getAttribute('data-level1-id'));
      return;
    }
    var l2btn = e.target.closest('[data-level2-trigger]');
    if (l2btn) {
      var l2Panel = l2btn.closest('.mega-panel');
      if (l2Panel) selectLevel2(l2Panel, l2btn.getAttribute('data-level2-id'));
    }
  });

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-mega-menu-trigger]');
    if (trigger) {
      var item = trigger.closest('[data-mega-menu-item]');
      var id = item.getAttribute('data-mega-menu-id');
      if (openPanelId === id) closeMegaPanel(id); else openMegaPanel(id);
      return;
    }
    // Click outside the open panel/trigger closes it
    if (openPanelId) {
      var openItem = document.querySelector('[data-mega-menu-id="' + openPanelId + '"]');
      if (openItem && !openItem.contains(e.target)) closeMegaPanel(openPanelId, { silent: true });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openPanelId) {
      closeMegaPanel(openPanelId);
      return;
    }
    var trigger = e.target.closest('[data-mega-menu-trigger]');
    if (trigger && e.key === 'ArrowDown') {
      e.preventDefault();
      var item = trigger.closest('[data-mega-menu-item]');
      var id = item.getAttribute('data-mega-menu-id');
      openMegaPanel(id);
      var panel = getPanelById(id);
      var firstItem = panel && panel.querySelector('.mega-panel__l1-item');
      if (firstItem) firstItem.focus();
    }
  });

  // Tab focus-trap inside whichever mega panel is currently open
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !openPanelId) return;
    var panel = getPanelById(openPanelId);
    if (!panel || !panel.contains(document.activeElement)) return;
    var focusable = getFocusable(panel);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ======================================================================
     MOBILE NAV — open / close
     ====================================================================== */
  function closeMobileNav(header) {
    var trigger = header.querySelector('[data-header-mobile-nav-trigger]');
    var mobileNav = header.querySelector('[data-header-mobile-nav]');
    if (!mobileNav) return;
    mobileNav.classList.remove('is-open'); // triggers the slide-out transition
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    window.setTimeout(function () {
      mobileNav.hidden = true; // only actually hide once the slide-out has finished
    }, 320); // matches --header-speed-slow
    if (trigger) trigger.focus();
  }

  function openMobileNav(header) {
    var trigger = header.querySelector('[data-header-mobile-nav-trigger]');
    var mobileNav = header.querySelector('[data-header-mobile-nav]');
    if (!mobileNav) return;
    mobileNav.hidden = false;
    void mobileNav.offsetWidth; // force reflow so the slide-in transition actually runs
    mobileNav.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    var activeScreen = mobileNav.querySelector('.header-mobile-nav__screen.is-active');
    var focusable = activeScreen && getFocusable(activeScreen);
    if (focusable && focusable.length) focusable[0].focus();
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-header-mobile-nav-trigger]');
    if (trigger) {
      var header = trigger.closest('[data-header]');
      if (!header) return;
      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) closeMobileNav(header); else openMobileNav(header);
      return;
    }
    var closeBtn = e.target.closest('[data-header-mobile-nav-close]');
    if (closeBtn) {
      var closeHeader = closeBtn.closest('[data-header]');
      if (closeHeader) closeMobileNav(closeHeader);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var openMobileNavEl = document.querySelector('[data-header-mobile-nav]:not([hidden])');
    if (!openMobileNavEl) return;
    var header = openMobileNavEl.closest('[data-header]');
    if (header) closeMobileNav(header);
  });

  // Tab focus-trap inside the open mobile nav
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var openMobileNav = document.querySelector('[data-header-mobile-nav]:not([hidden])');
    if (!openMobileNav || !openMobileNav.contains(document.activeElement)) return;
    var activeScreen = openMobileNav.querySelector('.header-mobile-nav__screen.is-active');
    if (!activeScreen) return;
    var focusable = getFocusable(activeScreen);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // Auto-close mobile nav if the viewport crosses into desktop width
  var desktopMql = window.matchMedia('(min-width: 990px)');
  function handleDesktopChange(e) {
    if (!e.matches) return;
    document.querySelectorAll('[data-header-mobile-nav-trigger][aria-expanded="true"]').forEach(function (trigger) {
      trigger.click();
    });
  }
  if (desktopMql.addEventListener) {
    desktopMql.addEventListener('change', handleDesktopChange);
  } else if (desktopMql.addListener) {
    desktopMql.addListener(handleDesktopChange); // older Safari fallback
  }

  /* ======================================================================
     PREDICTIVE SEARCH
     Debounced fetch against Shopify's predictive search JSON endpoint,
     rendered with product image + title, keyboard-navigable, cached
     per query string so re-typing something already searched is instant.
     ====================================================================== */
  (function initPredictiveSearch() {
    var debounceTimer = null;
    var currentController = null;
    var cache = {};

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str == null ? '' : str;
      return div.innerHTML;
    }

    function closeResults(form) {
      var results = form.querySelector('[data-predictive-search-results]');
      var input = form.querySelector('[data-predictive-search-input]');
      if (results) {
        results.hidden = true;
        results.innerHTML = '';
      }
      if (input) {
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
    }

    function renderLoading(results) {
      results.innerHTML = '<div class="site-header__search-results__loading" role="status">Searching…</div>';
      results.hidden = false;
    }

    function renderEmpty(results, term) {
      results.innerHTML = '<div class="site-header__search-results__empty" role="status">No results for “' + escapeHtml(term) + '”</div>';
      results.hidden = false;
    }

    function renderError(results) {
      results.innerHTML = '<div class="site-header__search-results__empty" role="status">Search is unavailable right now.</div>';
      results.hidden = false;
    }

    function renderResults(form, results, products, term) {
      if (!products.length) {
        renderEmpty(results, term);
        return;
      }
      var html = '<ul class="site-header__search-results__list" role="listbox">';
      products.forEach(function (p, index) {
        var img = (p.featured_image && p.featured_image.url) || p.image || results.closest('[data-predictive-search]')?.dataset.productPlaceholderUrl || '';
        var optionId = 'PredictiveSearchOption-' + index;
        html +=
          '<li class="site-header__search-results__item" role="presentation">' +
            '<a id="' + optionId + '" href="' + p.url + '" class="site-header__search-results__link" role="option" aria-selected="false">' +
              (img
                ? '<img class="site-header__search-results__image" src="' + img + '" alt="" width="48" height="48" loading="lazy">'
                : '<span class="site-header__search-results__image" aria-hidden="true"></span>') +
              '<span class="site-header__search-results__info">' +
                '<span class="site-header__search-results__title">' + escapeHtml(p.title) + '</span>' +
              '</span>' +
            '</a>' +
          '</li>';
      });
      html += '</ul>';

      var searchUrl = form.getAttribute('action');
      var input = form.querySelector('[data-predictive-search-input]');
      html += '<a href="' + searchUrl + '?q=' + encodeURIComponent(input.value) + '" class="site-header__search-results__viewall">View all results</a>';

      results.innerHTML = html;
      results.hidden = false;
    }

    function fetchResults(form, term) {
      var baseUrl = form.getAttribute('data-predictive-search-url');
      var results = form.querySelector('[data-predictive-search-results]');
      var input = form.querySelector('[data-predictive-search-input]');
      if (!baseUrl || !results || !input) return;

      if (cache[term]) {
        renderResults(form, results, cache[term], term);
        input.setAttribute('aria-expanded', 'true');
        return;
      }

      if (currentController) currentController.abort();
      currentController = ('AbortController' in window) ? new AbortController() : null;

      renderLoading(results);
      input.setAttribute('aria-expanded', 'true');

      var url = baseUrl + '.json?q=' + encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=6' +
        '&resources[options][unavailable_products]=last';

      fetch(url, currentController ? { signal: currentController.signal } : {})
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          cache[term] = products;
          renderResults(form, results, products, term);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return; // superseded by a newer keystroke, not a real failure
          renderError(results);
        });
    }

    document.addEventListener('input', function (e) {
      var input = e.target.closest('[data-predictive-search-input]');
      if (!input) return;
      var form = input.closest('[data-predictive-search]');
      if (!form) return;

      var term = input.value.trim();
      window.clearTimeout(debounceTimer);

      if (term.length < 2) {
        closeResults(form);
        return;
      }

      debounceTimer = window.setTimeout(function () {
        fetchResults(form, term);
      }, 250);
    });

    document.addEventListener('keydown', function (e) {
      var input = e.target.closest('[data-predictive-search-input]');
      if (!input) return;
      var form = input.closest('[data-predictive-search]');
      var results = form && form.querySelector('[data-predictive-search-results]');
      if (!results || results.hidden) return;

      if (e.key === 'Escape') {
        closeResults(form);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var links = Array.prototype.slice.call(results.querySelectorAll('.site-header__search-results__link'));
        if (!links.length) return;
        var activeIdx = -1;
        links.forEach(function (l, i) { if (l.classList.contains('is-active')) activeIdx = i; });
        links.forEach(function (l) { l.classList.remove('is-active'); l.setAttribute('aria-selected', 'false'); });
        var next = e.key === 'ArrowDown' ? activeIdx + 1 : activeIdx - 1;
        if (next < 0) next = links.length - 1;
        if (next >= links.length) next = 0;
        links[next].classList.add('is-active');
        links[next].setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', links[next].id);
        links[next].scrollIntoView({ block: 'nearest' });
        return;
      }
      if (e.key === 'Enter') {
        var activeLink = results.querySelector('.site-header__search-results__link.is-active');
        if (activeLink) {
          e.preventDefault();
          window.location.href = activeLink.getAttribute('href');
        }
        // otherwise let the form submit normally to the full search results page
      }
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-predictive-search]')) return; // inside the form/results, leave it open
      document.querySelectorAll('[data-predictive-search]').forEach(closeResults);
    });
  })();
})();
